// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SignatureService } from '@/services/signatureService';
import { SignatureVerificationData } from '@/types/signature';
import { formatDate, generateQRCode, generateHash } from '@/utils/signatureUtils';
import { SignatureReport } from './SignatureReport';
import { PDFGenerator } from '@/utils/pdfGenerator';
import { 
  FileText, 
  User, 
  Calendar, 
  Globe, 
  Smartphone, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Download,
  QrCode,
  Loader2,
  AlertTriangle,
  Shield,
  Eye,
  Mail
} from 'lucide-react';

export const DocumentVerification: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [verificationData, setVerificationData] = useState<SignatureVerificationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      loadVerificationData();
    }
  }, [token]);

  useEffect(() => {
    if (verificationData?.verification_url) {
      setQrCodeUrl(verificationData.verification_url);
    } else if (token) {
      // Gerar QR Code para a URL de verificação
      const verificationLink = `${window.location.origin}/verificar/${token}`;
      generateQRCode(verificationLink).then(setQrCodeUrl);
    }
  }, [verificationData?.verification_url, token]);

  const loadVerificationData = async () => {
    try {
      setLoading(true);
      const data = await SignatureService.getVerificationData(token!);
      
      if (!data) {
        setError('Documento não encontrado ou link inválido.');
        return;
      }

      setVerificationData(data);
    } catch (err) {
      console.error('Erro ao carregar dados de verificação:', err);
      setError('Erro ao carregar dados de verificação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    if (verificationData?.assinatura.url_pdf_assinado) {
      const link = document.createElement('a');
      link.href = verificationData.assinatura.url_pdf_assinado;
      link.download = `documento_assinado_${verificationData.documento.titulo.replace(/\s+/g, '_')}.pdf`;
      link.click();
    }
  };

  const handleDownloadReport = async () => {
    if (!verificationData) return;
    
    try {
      // Gerar relatório completo
      const reportPDFBytes = await PDFGenerator.generateSignatureReport({
        documento: {
          titulo: verificationData.documento.titulo,
          id: verificationData.documento.id,
          data_criacao: verificationData.documento.data_criacao,
          hash_original: verificationData.assinatura.hash_pdf || 'N/A'
        },
        assinatura: {
          nome_assinante: verificationData.assinatura.nome_assinante,
          email_assinante: verificationData.assinatura.email_assinante,
          data_assinatura: verificationData.assinatura.data_assinatura,
          ip: verificationData.assinatura.ip_assinante,
          user_agent: verificationData.assinatura.user_agent,
          token: verificationData.assinatura.token_confirmacao,
          assinatura_base64: verificationData.assinatura.assinatura_base64,
          foto_base64: verificationData.assinatura.foto_base64
        },
        verification_url: verificationData.verification_url
      });
      
      // Criar blob e download
      const blob = new Blob([reportPDFBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio_assinatura_${verificationData.documento.titulo.replace(/\s+/g, '_')}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
    }
  };

  const getStatusBadge = () => {
    if (!verificationData) return null;

    if (verificationData.is_valid) {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle className="w-4 h-4 mr-1" />
          Válido
        </Badge>
      );
    } else {
      return (
        <Badge variant="destructive">
          <XCircle className="w-4 h-4 mr-1" />
          Inválido
        </Badge>
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Verificando documento...</p>
        </div>
      </div>
    );
  }

  if (error || !verificationData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Erro na Verificação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { documento, assinatura, is_valid, verification_url } = verificationData;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Verificação de Assinatura
          </h1>
          <p className="text-gray-600">
            Verifique a autenticidade do documento assinado
          </p>
        </div>

        {/* Status */}
        <div className="flex justify-center mb-6">
          {getStatusBadge()}
        </div>

        {/* Opções de Download */}
        {is_valid && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Download className="w-5 h-5 mr-2" />
                Baixar Documentos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    const url = `${window.location.origin}/api/documents/download-signed-pdf?documentId=${documento.id}&signatureId=${assinatura.id}`;
                    window.open(url, '_blank');
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF Assinado (Original + Assinatura)
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    const url = `${window.location.origin}/api/documents/download-complete-pdf?documentId=${documento.id}&signatureId=${assinatura.id}`;
                    window.open(url, '_blank');
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF Completo (Original + Assinatura + Relatório)
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Relatório de Verificação */}
        <SignatureReport 
          documento={documento}
          assinatura={assinatura}
          qrCodeUrl={qrCodeUrl}
          onDownloadReport={handleDownloadReport}
        />
      </div>
    </div>
  );
};
